import { useEffect, useMemo, useState } from 'react';
import { Banner, Button, Card, Page, Text } from '@shopify/polaris';
import { MetricCard } from '../components/MetricCard';
import { getSyncStatusLabel, getSyncTypeLabel } from '../lib/syncLabels';

function formatDateTime(value?: string | null) {
  if (!value) return '—';
  const date = new Date(value);
  return Number.isNaN(date.getTime()) ? '—' : date.toLocaleString('uk-UA');
}

export function SyncPage({ api, overview, onRefreshOverview, readOnly = false }: any) {
  const [syncStatus, setSyncStatus] = useState<any>(null);
  const [syncLog, setSyncLog] = useState<any>(null);
  const [error, setError] = useState('');
  const [busyAction, setBusyAction] = useState('');
  const [message, setMessage] = useState('');
  const [refreshing, setRefreshing] = useState(false);
  const [lastRefreshedAt, setLastRefreshedAt] = useState<string | null>(null);

  const refresh = async (options: { quiet?: boolean } = {}) => {
    if (!options.quiet) {
      setRefreshing(true);
      setMessage('');
    }

    try {
      const [statusResponse, logResponse] = await Promise.all([
        api.getSyncStatus(),
        api.getSyncLog(20, true),
      ]);
      setSyncStatus(statusResponse);
      setSyncLog(logResponse);
      setLastRefreshedAt(new Date().toISOString());
      setError('');
      if (!options.quiet) {
        setMessage('Дані синхронізації оновлено.');
      }
    } catch (nextError) {
      setError(nextError instanceof Error ? nextError.message : 'Не вдалося завантажити синхронізацію');
    } finally {
      if (!options.quiet) {
        setRefreshing(false);
      }
    }
  };

  useEffect(() => {
    void refresh({ quiet: true });
  }, []);

  const actionDisabledReason = useMemo(() => {
    if (syncStatus?.isRunning) return 'Зараз уже виконується інша синхронізація';
    return '';
  }, [syncStatus]);

  const runAction = async (action: 'full' | 'queued') => {
    setBusyAction(action);
    setMessage('');
    try {
      if (action === 'full') {
        await api.runFullSync();
        setMessage('Повна синхронізація запущена.');
      } else {
        await api.runQueuedExport();
        setMessage('Черга замовлень передана серверу синхронізації для 1С.');
      }
      await refresh({ quiet: true });
      await onRefreshOverview();
    } catch (nextError) {
      setError(nextError instanceof Error ? nextError.message : 'Ручна дія не виконалась');
    } finally {
      setBusyAction('');
    }
  };

  return (
    <Page title="Синхронізація" subtitle="Каденс, останні прогони й ручні дії">
      <div className="admin-stack">
        {error ? <Banner tone="critical" title="Синхронізація не читається"><p>{error}</p></Banner> : null}
        {message ? <Banner tone="success" title="Дію виконано"><p>{message}</p></Banner> : null}
        {readOnly ? (
          <Banner tone="info" title="Роль перегляду">
            <p>Ручні дії вимкнені. Ви бачите стан синхронізації, але не керуєте сервером синхронізації.</p>
          </Banner>
        ) : null}

        <div className="admin-grid admin-grid--metrics">
          <MetricCard label="Повна синхронізація" value={`${syncStatus?.scheduler?.intervalMinutes ?? overview?.schedulers?.fullSync?.intervalMinutes ?? '—'} хв`} summary="Основний цикл" status={syncStatus?.scheduler?.running ? 'ok' : 'disabled'} />
          <MetricCard label="Черга замовлень" value={`${syncStatus?.queuedOrdersScheduler?.intervalMinutes ?? overview?.schedulers?.queuedOrders?.intervalMinutes ?? '—'} хв`} summary="Передача замовлень у 1С" status={syncStatus?.queuedOrdersScheduler?.running ? 'ok' : 'disabled'} />
          <MetricCard label="Перевірка чернеток" value={`${syncStatus?.draftPublishRecheckScheduler?.intervalMinutes ?? overview?.schedulers?.draftPublishRecheck?.intervalMinutes ?? '—'} хв`} summary="Повторна перевірка фото" status={syncStatus?.draftPublishRecheckScheduler?.running ? 'ok' : 'disabled'} />
        </div>

        <Card>
          <div className="admin-section-head">
            <Text as="h2" variant="headingMd">Ручні дії</Text>
            <Button onClick={() => { void refresh(); }} loading={refreshing}>Оновити</Button>
          </div>
          <div className="admin-inline-actions">
            <Button variant="primary" onClick={() => runAction('full')} loading={busyAction === 'full'} disabled={readOnly || Boolean(actionDisabledReason)}>Запустити повну синхронізацію</Button>
            <Button onClick={() => runAction('queued')} loading={busyAction === 'queued'} disabled={readOnly}>Передати замовлення з черги</Button>
          </div>
          {actionDisabledReason ? <Text as="p" variant="bodySm" tone="subdued">{actionDisabledReason}</Text> : null}
          {lastRefreshedAt ? <Text as="p" variant="bodySm" tone="subdued">Оновлено: {formatDateTime(lastRefreshedAt)}</Text> : null}
        </Card>

        <Card>
          <div className="admin-stack">
            <Text as="h2" variant="headingMd">Історія синхронізацій</Text>
            <div className="admin-table-wrap">
              <table className="admin-table">
                <thead>
                  <tr>
                    <th>Тип</th>
                    <th>Початок</th>
                    <th>Завершення</th>
                    <th>Статус</th>
                    <th>Оброблено</th>
                    <th>Створено</th>
                    <th>Оновлено</th>
                    <th>Помилки</th>
                  </tr>
                </thead>
                <tbody>
                  {(syncLog?.history || []).map((row: any) => {
                    const type = getSyncTypeLabel(row.sync_type);
                    const status = getSyncStatusLabel(row.status);
                    return (
                    <tr key={row.id}>
                      <td>
                        <strong>{type.label}</strong>
                        <div className="admin-table__hint">{type.description}</div>
                      </td>
                      <td>{formatDateTime(row.started_at)}</td>
                      <td>{formatDateTime(row.completed_at)}</td>
                      <td title={status.description}>{status.label}</td>
                      <td>{row.items_processed ?? 0}</td>
                      <td>{row.items_created ?? 0}</td>
                      <td>{row.items_updated ?? 0}</td>
                      <td>{row.items_failed ?? 0}</td>
                    </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          </div>
        </Card>
      </div>
    </Page>
  );
}
